import { subscribeDialog } from '@src/components/Dialog/Dialog';
import { trackFrameGift } from '@src/utils/frameGiftAnalytics';
import { PersonalGiftCheckout } from './PersonalGiftCheckout';
import { PersonalGiftReceipt } from './PersonalGiftReceipt';
import { useAppDispatch } from '@src/state/hooks';
import { giftOpened } from '@src/state/boxSlice';
import { useContext, useEffect, useRef, useState } from 'react';
import { BoxPreviewContext } from './BoxPreviewContext';
import type { BoxInfo, BoxGiftInfo, BoxGiftHistory } from '@lub0v/santa-utils';
import { useTranslation } from 'react-i18next';
import { showDialog, showToast } from '@src/components';
import { ThemeArtwork } from '@src/theme/Artwork';
import { BoxGiftDialog, type BoxGiftScreen } from './BoxGiftDialog';
import { trackGiftEvent } from '@src/utils/giftAnalytics';
import './BoxLandscapeGift.css';

export function BoxLandscapeGift({ info }: { info: BoxInfo }) {
  const { t } = useTranslation();
  const preview = useContext(BoxPreviewContext);
  const dispatch = useAppDispatch();
  const scope = `${info.box.key}:${info.cardId}`;
  const [received, setReceived] = useState<{ scope: string; gift: BoxGiftInfo | null } | null>(null);
  const setOpened = (gift: BoxGiftInfo | null) => setReceived({ scope, gift });
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const gift = received?.scope === scope ? received.gift : info.gift;
  const refreshVersion = useRef(0);
  useEffect(() => {
    if (preview || !(info.isAdmin || info.isParticipant)) return;
    let disposed = false;
    let checking = false;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Clear the previous game status before subscribing to its gift updates.
    setReceived(null);
    const refresh = async () => {
      if (checking || inFlight.current || document.visibilityState === 'hidden') return;
      checking = true;
      const version = refreshVersion.current;
      try {
        const response = await fetch(`/api/box/${encodeURIComponent(info.box.key)}/gift/hub`, {
          credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        });
        if (!response.ok) return;
        const hub = await response.json();
        if (!disposed && version === refreshVersion.current && !inFlight.current)
          setReceived({ scope, gift: hub.gift ?? null });
      } catch {
        // Retain the last known receipt when a background refresh fails.
      } finally { checking = false; }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30000);
    const onVisible = () => void refresh();
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      controller.abort();
      clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [preview, scope, info.box.key, info.isAdmin, info.isParticipant, info.gift]);
  const returnOpened = useRef(false);
  const onDismiss = useRef<(() => Promise<void>) | null>(null);
  useEffect(()=>{
    if(!preview && !returnOpened.current && (gift || info.giftHistory || new URLSearchParams(window.location.search).get('personal') === '1') && (info.isAdmin || info.isParticipant) && /\/gifted$/.test(window.location.pathname)) {
      returnOpened.current=true;void open(true);
    }
  },[preview,gift,info.isAdmin,info.isParticipant]);
  if ((!gift && !info.giftHistory) || !(info.isAdmin || info.isParticipant)) return null;

  function saveFrameOnClose() {
    // Keep the draft through screen changes and the thank-you confirmation.
    // Closing via X, Escape, or a footer action all publish a null dialog.
    const stop = subscribeDialog(next => {
      if (next) return;
      stop();
      const save = onDismiss.current;
      onDismiss.current = null;
      void save?.().catch(() => showToast(t('boxGift.frameSaveError')));
    });
  }

  async function open(fromCheckout = false) {
    if (preview || inFlight.current) return;
    inFlight.current = true;
    refreshVersion.current += 1;
    setBusy(true);
    try {
      if (fromCheckout && new URLSearchParams(window.location.search).get('personal') === '1') {
        const payment=Number(new URLSearchParams(window.location.search).get('payment'));
        if (!Number.isSafeInteger(payment) || payment <= 0) throw Error();
        showDialog({title:t('game.ward.framePreviewTitle'),mobileFullScreen:true,testId:'personal-gift-payment-dialog',children:<PersonalGiftCheckout boxKey={info.box.key} payment={payment}/>});
        return;
      }
      let current = gift ?? null;
      let history: BoxGiftHistory | null = info.giftHistory ?? null;
      if (!fromCheckout) {
        const response = await fetch(`/api/box/${encodeURIComponent(info.box.key)}/gift/hub`, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Gift history unavailable');
        const hub = await response.json();
        current = hub.gift;
        history = hub.history;
        if (!current && !history) throw new Error('Gift unavailable');
        setOpened(current);
      }
      if (!fromCheckout && current?.personal) trackFrameGift('gift_frame_received_clicked', { first_open: !current.openedAt });
      if (!fromCheckout && current && !current.openedAt) {
        const response = await fetch(`/api/box/${encodeURIComponent(info.box.key)}/gift/open`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({giftId:current.id}),
        });
        if (!response.ok) throw new Error('Gift unavailable');
        current = (await response.json()) as BoxGiftInfo;
        if (current!.receiptRecorded) {
          trackGiftEvent('santa_gift_received');
        }
        setOpened(current);
        dispatch(giftOpened({ key: info.box.key, cardId: info.cardId, gift: current! }));
      }
      // Personal receipts retain their own flow after thanks, even when this user
      // has also sent gifts and therefore has a sender history.
      if (current?.personal) {
        const receipt = current;
        onDismiss.current = null;
        let submitThanks: (() => Promise<void>) | undefined;
        showDialog({
          title: t('game.ward.framePreviewTitle'),
          mobileFullScreen: true,
          testId: 'personal-gift-receipt-dialog',
          submit: t(receipt.thankedAt ? 'game.ward.personalGiftBack' : 'boxGift.sendThank'),
          onSubmit: receipt.thankedAt ? undefined : async () => {
            if (!submitThanks) throw new Error('Receipt is not ready');
            await submitThanks();
          },
          renderBody: busy => <PersonalGiftReceipt boxKey={info.box.key} gift={receipt} busy={busy}
            registerSubmit={submit => { submitThanks = submit; }}
            registerFrameSave={save => { onDismiss.current = save; }}
            onUpdated={updated => { setOpened(updated); dispatch(giftOpened({key:info.box.key,cardId:info.cardId,gift:updated})); }} />,
        });
        saveFrameOnClose();
        return;
      }
      // Personal purchases have zero random-box recipients in the summary.
      // Reopen their confirmed checkout instead of the random-box gift hub.
      if (!current && history?.summary.boxCount === 0) {
        showDialog({title:t('game.ward.personalGiftSent'),mobileFullScreen:true,testId:'personal-gift-payment-dialog',children:<PersonalGiftCheckout boxKey={info.box.key} payment={history.paymentId}/>});
        return;
      }
      const canDismissReceipt = !fromCheckout && !history && current && !current.thankedAt;
      let interacted = false;
      let dismissalTracked = false;
      const dismissReceipt = (dismissal: 'close' | 'continue') => {
        if (canDismissReceipt && !interacted && !dismissalTracked) {
          dismissalTracked = true;
          trackGiftEvent('santa_gift_dismissed', { screen: 'gift', dismissal });
        }
      };
      const showGift = (screen: BoxGiftScreen) => showDialog({
        title: t(screen === 'payment' ? 'boxGift.paymentTitle' : (screen === 'sent' || screen === 'hub') ? 'boxGift.sentTitle' : screen === 'thanked' ? 'boxGift.thankSent' : screen === 'thanks' ? 'boxGift.thankTitle' : screen === 'give' ? 'boxGift.spread' : 'boxGift.title'),
        onCancel: () => { dismissReceipt('close'); },
        children: <BoxGiftDialog onGiftInteraction={action => { if (action === 'continue') dismissReceipt('continue'); else interacted = true; }} onDismiss={onDismiss} gift={current} history={history} initialScreen={fromCheckout ? 'payment' : history ? 'hub' : current?.thankedAt ? 'thanked' : 'gift'} boxKey={info.box.key} onGiftUpdated={(updated) => {
          current = updated;
          setOpened(updated);
          dispatch(giftOpened({ key: info.box.key, cardId: info.cardId, gift: updated }));
        }} onScreenChange={showGift} />,
        mobileFullScreen: screen !== 'sent' && screen !== 'hub',
        testId: 'box-gift-dialog',
      });
      onDismiss.current = null;
      showGift(fromCheckout ? 'payment' : history ? 'hub' : current?.thankedAt ? 'thanked' : 'gift');
      saveFrameOnClose();
    } catch {
      showToast(t('boxGift.error'));
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  }

  return (
    <button
      type="button"
      className="box-landscape-gift"
      data-testid="box-landscape-gift"
      data-unopened={!!gift && !gift.openedAt}
      aria-label={t(gift && !gift.openedAt ? 'boxGift.open' : 'boxGift.revisit')}
      title={t(gift && !gift.openedAt ? 'boxGift.open' : 'boxGift.revisit')}
      disabled={busy}
      aria-busy={busy}
      onClick={() => void open()}
    >
      <ThemeArtwork role="gift" className="box-landscape-gift__art" />
    </button>
  );
}
